<template>
  <div class="classic-layout">
    <section class="hero-section">
      <div
        class="hero-background"
        :style="{
          backgroundImage: `url(${cdnUrl}/lightning-model-page/cx/city-backdrop.jpg)`,
        }"
      >
        <v-container class="hero-container">
          <v-row class="align-center justify-center">
            <v-col cols="12" class="text-center py-8">
              <div class="hero-badge mb-4">
                <v-icon start size="small">mdi-calendar</v-icon>
                {{ modelData.year }} Model
              </div>
              <h1 class="hero-title mb-4">
                {{ modelData.make }} {{ modelData.model }}
              </h1>
              <p v-if="selectedTrim" class="hero-trim-name mb-2">
                {{ selectedTrim.trim || selectedTrim.styleName }}
              </p>
              <div class="hero-price-container">
                <span class="hero-price-label">{{
                  selectedTrim ? 'Price' : 'Starting at'
                }}</span>
                <span class="hero-price">{{
                  formatCurrency(
                    selectedTrim
                      ? selectedTrim.baseMsrp || selectedTrim.price || 0
                      : lowestPrice,
                  )
                }}</span>
              </div>
            </v-col>
          </v-row>

          <v-row class="justify-center">
            <v-col cols="12" lg="10" xl="8">
              <div class="hero-image-wrapper">
                <v-img
                  :src="currentVehicleImage"
                  :alt="`${modelData.year} ${modelData.make} ${modelData.model}`"
                  class="hero-vehicle-image"
                  cover
                >
                  <template v-slot:placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular indeterminate color="white" />
                    </div>
                  </template>
                </v-img>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </section>

    <section class="stats-section py-8">
      <v-container>
        <v-row>
          <v-col cols="6" sm="3" v-for="stat in quickStats" :key="stat.label">
            <div class="stat-card text-center">
              <v-icon
                :icon="stat.icon"
                size="large"
                color="primary"
                class="mb-2"
              />
              <v-tooltip
                :text="stat.value"
                location="top"
                :disabled="stat.value.length <= 15"
              >
                <template v-slot:activator="{ props }">
                  <div class="stat-value" v-bind="props">{{ stat.value }}</div>
                </template>
              </v-tooltip>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section
      v-if="availableColors.length > 0"
      class="color-section py-8 bg-grey-lighten-5"
    >
      <v-container>
        <h2 class="section-title text-center mb-6">
          <v-icon start>mdi-palette</v-icon>
          Available Colors
        </h2>
        <v-row justify="center">
          <v-col
            cols="auto"
            v-for="(color, index) in availableColors"
            :key="index"
          >
            <v-tooltip
              :text="String(color.colorName || color.name || 'Color')"
              location="top"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  :style="{ backgroundColor: color.hex || '#ccc' }"
                  @click="$emit('select-color', color)"
                  :class="{
                    'selected-color': selectedColor?.hex === color.hex,
                  }"
                  size="large"
                  elevation="3"
                  class="color-swatch"
                >
                  <v-icon v-if="selectedColor?.hex === color.hex" color="white">
                    mdi-check
                  </v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="trims-section py-10">
      <v-container>
        <div class="section-header mb-8">
          <h2 class="section-title">
            <v-icon start>mdi-car-multiple</v-icon>
            Available Trims
          </h2>
          <p class="section-subtitle">
            Choose from {{ modelData.trims.length }} configurations
          </p>
        </div>

        <div class="table-container">
          <v-table class="trims-table">
            <thead>
              <tr>
                <th class="trim-column-header">Trim</th>
                <th>Price</th>
                <th>Engine</th>
                <th>MPG (City/Hwy)</th>
                <th>Key Features</th>
                <th v-if="showTrimComparing !== false" class="text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(trim, index) in modelData.trims"
                :key="trim.id || index"
                :class="{ 'selected-row': selectedTrim?.id === trim.id }"
                @click="$emit('select-trim', trim)"
                class="trim-row"
              >
                <td>
                  <div class="d-flex align-center">
                    <div class="trim-image-wrapper">
                      <v-img
                        :src="trim.img || noVehicleImage"
                        :alt="`${trim.trim || trim.styleName} image`"
                        class="trim-thumbnail"
                        contain
                      >
                        <template v-slot:placeholder>
                          <div
                            class="d-flex align-center justify-center fill-height"
                          >
                            <v-progress-circular
                              indeterminate
                              size="16"
                              width="2"
                            />
                          </div>
                        </template>
                      </v-img>
                    </div>
                    <div class="trim-info">
                      <v-tooltip
                        :text="trim.trim || trim.styleName || ''"
                        location="top"
                        :disabled="
                          !isTrimNameTruncated(
                            trim.trim || trim.styleName || '',
                          )
                        "
                      >
                        <template v-slot:activator="{ props }">
                          <div class="trim-name" v-bind="props">
                            {{ trim.trim || trim.styleName }}
                          </div>
                        </template>
                      </v-tooltip>
                      <v-chip
                        v-if="selectedTrim?.id === trim.id"
                        color="primary"
                        size="x-small"
                        variant="flat"
                        class="mt-1 selected-badge"
                        density="comfortable"
                      >
                        <v-icon start size="x-small">mdi-check</v-icon>
                        Selected
                      </v-chip>
                    </div>
                  </div>
                </td>
                <td class="font-weight-bold">
                  {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
                </td>
                <td>
                  <v-tooltip
                    :text="getEngineInfo(trim)"
                    location="top"
                    :disabled="getEngineInfo(trim).length <= 25"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="table-cell-ellipsis">
                        {{
                          getEngineInfo(trim).length > 25
                            ? getEngineInfo(trim).substring(0, 25) + '...'
                            : getEngineInfo(trim)
                        }}
                      </span>
                    </template>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip
                    :text="getMpgInfo(trim)"
                    location="top"
                    :disabled="getMpgInfo(trim).length <= 20"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="table-cell-ellipsis">
                        {{
                          getMpgInfo(trim).length > 20
                            ? getMpgInfo(trim).substring(0, 20) + '...'
                            : getMpgInfo(trim)
                        }}
                      </span>
                    </template>
                  </v-tooltip>
                </td>
                <td>
                  <div class="features-chips">
                    <v-tooltip
                      v-for="(feature, idx) in getTopFeatures(trim).slice(0, 3)"
                      :key="idx"
                      :text="feature"
                      location="top"
                      :disabled="feature.length <= 20"
                    >
                      <template v-slot:activator="{ props }">
                        <v-chip
                          size="x-small"
                          variant="outlined"
                          class="mr-1 feature-chip"
                          v-bind="props"
                        >
                          {{
                            feature.length > 20
                              ? feature.substring(0, 20) + '...'
                              : feature
                          }}
                        </v-chip>
                      </template>
                    </v-tooltip>
                  </div>
                </td>
                <td v-if="showTrimComparing !== false" class="text-center">
                  <v-btn
                    :color="isInComparison(trim.id) ? 'success' : 'primary'"
                    :variant="isInComparison(trim.id) ? 'tonal' : 'outlined'"
                    size="small"
                    @click.stop="$emit('add-to-comparison', trim)"
                    :icon="isInComparison(trim.id) ? 'mdi-check' : 'mdi-plus'"
                  />
                  <v-tooltip
                    :text="
                      isInComparison(trim.id)
                        ? 'Remove from comparison'
                        : 'Add to comparison'
                    "
                    location="top"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props"></span>
                    </template>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-container>
    </section>

    <section
      v-if="showTrimComparing !== false && comparisonTrims.length > 1"
      class="comparison-section py-10 bg-grey-lighten-5"
    >
      <v-container>
        <div class="section-header mb-8">
          <h2 class="section-title">
            <v-icon start>mdi-compare</v-icon>
            Trim Comparison
          </h2>
          <p class="section-subtitle">
            Side-by-side comparison of {{ comparisonTrims.length }} selected
            trims
          </p>
        </div>

        <EnhancedTrimComparison
          :trims="comparisonTrims"
          :modelData="modelData"
          @remove-trim="$emit('remove-from-comparison', $event)"
          @clear-comparison="$emit('clear-comparison')"
          @diff-toggle="$emit('diff-toggle', $event)"
        />
      </v-container>
    </section>

    <section
      v-if="showModelComparing !== false"
      class="model-comparison-section py-10"
    >
      <v-container>
        <div class="section-header mb-8">
          <h2 class="section-title">
            <v-icon start>mdi-car-compare</v-icon>
            Compare With Other Models
          </h2>
          <p class="section-subtitle">
            See how this model stacks up against competitors
          </p>
        </div>

        <slot name="model-comparison"></slot>
      </v-container>
    </section>

    <section v-if="selectedTrim" class="details-section py-10">
      <v-container>
        <div class="section-header mb-8">
          <h2 class="section-title">
            <v-icon start>mdi-information</v-icon>
            {{ selectedTrim.trim || selectedTrim.styleName }} Details
          </h2>
        </div>

        <v-tabs v-model="activeTab" class="mb-6">
          <v-tab value="specs">
            <v-icon start>mdi-cog</v-icon>
            Specifications
          </v-tab>
          <v-tab value="features">
            <v-icon start>mdi-star</v-icon>
            Features
          </v-tab>
          <v-tab value="options">
            <v-icon start>mdi-tune</v-icon>
            Options
          </v-tab>
          <v-tab value="warranty">
            <v-icon start>mdi-shield-check</v-icon>
            Warranty
          </v-tab>
        </v-tabs>

        <v-tabs-window v-model="activeTab">
          <v-tabs-window-item value="specs">
            <SpecificationsList :trim="selectedTrim" />
          </v-tabs-window-item>

          <v-tabs-window-item value="features">
            <FeaturesList :trim="selectedTrim" />
          </v-tabs-window-item>

          <v-tabs-window-item value="options">
            <OptionsList :trim="selectedTrim" />
          </v-tabs-window-item>

          <v-tabs-window-item value="warranty">
            <WarrantyInfo :trim="selectedTrim" :modelData="modelData" />
          </v-tabs-window-item>
        </v-tabs-window>
      </v-container>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import EnhancedTrimComparison from '@/components/EnhancedTrimComparison.vue'
import SpecificationsList from '@/components/SpecificationsList.vue'
import FeaturesList from '@/components/FeaturesList.vue'
import OptionsList from '@/components/OptionsList.vue'
import WarrantyInfo from '@/components/WarrantyInfo.vue'
import { formatCurrency, getCDNUrl } from '@/utils'
import {
  getMpgInfo as getMpgInfoUtil,
  parseSpecValue,
  parseSpecItem,
} from '@/utils/modelDetails'
import type {
  TrimData,
  ColorOption,
  ModelData,
  QuickStat,
  SpecificationCategory,
} from '@/types'

const props = defineProps<{
  modelData: ModelData
  selectedTrim: TrimData | null
  selectedColor: ColorOption | null
  comparisonTrims: TrimData[]
  availableColors: ColorOption[]
  lowestPrice: number
  currentVehicleImage: string
  showTrimComparing?: boolean
  showModelComparing?: boolean
}>()

defineEmits<{
  'select-trim': [trim: TrimData]
  'select-color': [color: ColorOption]
  'add-to-comparison': [trim: TrimData]
  'remove-from-comparison': [trimId: string]
  'clear-comparison': []
  'diff-toggle': [enabled: boolean]
}>()

const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`
const activeTab = ref('specs')

const quickStats = computed((): QuickStat[] => {
  const activeTrim = props.selectedTrim || props.modelData?.trims?.[0]
  return [
    {
      icon: 'mdi-gas-station',
      value: getMpgInfo(activeTrim) || 'N/A',
      label: 'Fuel Economy',
    },
    {
      icon: 'mdi-engine',
      value: getHorsepower(activeTrim) || 'N/A',
      label: 'Horsepower',
    },
    {
      icon: 'mdi-seat',
      value: getSeating(activeTrim) || 'N/A',
      label: 'Seating',
    },
    {
      icon: 'mdi-shield-check',
      value: '5 Star',
      label: 'Safety Rating',
    },
  ]
})

const isInComparison = (trimId: string): boolean => {
  return props.comparisonTrims.some(t => t.id === trimId)
}

const getSpecValue = (
  trim: TrimData | null | undefined,
  category: string,
  searchTerm: string,
): string => {
  const tech = trim?.technicalSpecification
  const categoryData = tech?.[category] as SpecificationCategory | undefined
  const items = categoryData?.items || []
  const item = items.find((i: string) => i.includes(searchTerm))
  if (item) {
    const value = parseSpecValue(item)
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    return value
  }
  return 'N/A'
}

const getEngineInfo = (trim: TrimData | null | undefined): string => {
  const tech = trim?.technicalSpecification
  const engineData = tech?.Engine as SpecificationCategory | undefined
  const engineItems = engineData?.items || []
  if (engineItems.length > 0) {
    const engineStr = engineItems[0]
    const match = engineStr.match(/(\d+\.\d+L|\d+L)/i)
    if (match) return match[0]
    const parsed = parseSpecItem(engineStr, false)
    return (typeof parsed === 'object' ? parsed.label : engineStr) || 'Engine'
  }
  return 'Engine'
}

const getMpgInfo = getMpgInfoUtil

const getHorsepower = (trim: TrimData | null | undefined): string => {
  let hp = getSpecValue(trim, 'Engine', 'SAE Net Horsepower')
  if (!hp || hp === 'N/A') {
    hp = getSpecValue(trim, 'Engine', 'Horsepower')
  }
  if (!hp || hp === 'N/A') {
    hp = getSpecValue(trim, 'Engine', 'HP')
  }
  if (hp && hp !== 'N/A') {
    const match = hp.match(/\d+/)
    return match ? `${match[0]} HP` : hp
  }
  return 'HP N/A'
}

const getSeating = (trim: TrimData | null | undefined): string => {
  const seating =
    getSpecValue(trim, 'Interior Dimensions', 'Passenger') ||
    getSpecValue(trim, 'Summary', 'Passenger')
  if (seating && seating !== 'N/A') {
    const match = seating.match(/\d+/)
    return match ? `${match[0]} Seats` : seating
  }
  return 'Seating N/A'
}

const sanitizeHtml = (text: string): string => {
  if (!text) return ''
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
}

const getTopFeatures = (trim: TrimData | null | undefined): string[] => {
  const features: string[] = []
  const tech = trim?.technicalSpecification

  const engineData = tech?.Engine as SpecificationCategory | undefined
  const engineItems = engineData?.items || []
  const engine = engineItems[0]
  if (engine) {
    const parsed = parseSpecItem(engine, false)
    const engineLabel =
      typeof parsed === 'object' ? parsed.label || engine : engine
    features.push(sanitizeHtml(engineLabel))
  }

  const transData = tech?.Transmission as SpecificationCategory | undefined
  const transItems = transData?.items || []
  const drivetrain = transItems.find((item: string) =>
    item.includes('Drivetrain'),
  )
  if (drivetrain) {
    const value = parseSpecValue(drivetrain)
    if (value && value !== 'N/A') features.push(sanitizeHtml(value))
  }

  const interiorData = tech?.['Interior Dimensions'] as
    | SpecificationCategory
    | undefined
  const interiorItems = interiorData?.items || []
  const seating = interiorItems.find((item: string) =>
    item.includes('Passenger Capacity'),
  )
  if (seating) {
    const value = parseSpecValue(seating)
    if (value && value !== 'N/A') {
      const sanitizedValue = sanitizeHtml(value)
      features.push(`${sanitizedValue} Seats`)
    }
  }

  return features
}

const isTrimNameTruncated = (text: string): boolean => {
  // Estimate if text is likely to be truncated based on character count
  // Approximate 250px can fit about 25-30 characters depending on font
  return text.length > 28
}
</script>

<style scoped>
.classic-layout {
  width: 100%;
}

.hero-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.hero-background {
  background-size: cover;
  background-position: center;
  background-blend-mode: soft-light;
  padding: 60px 0 80px;
  position: relative;
}

.hero-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(102, 126, 234, 0.9) 0%,
    rgba(118, 75, 162, 0.8) 100%
  );
  z-index: 1;
}

.hero-container {
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  color: white;
  text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: -1px;
}

.hero-trim-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-price-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.hero-price-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.hero-price {
  font-size: 2.25rem;
  font-weight: 700;
  color: white;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.2);
}

.hero-image-wrapper {
  max-width: 100%;
  margin-top: 40px;
}

.hero-vehicle-image {
  filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4));
  border-radius: 8px;
}

.stats-section {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.stat-card {
  padding: 16px;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  margin: 8px 0 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.color-section {
  position: relative;
}

.color-swatch {
  border: 3px solid white;
  transition: all 0.3s ease;
}

.color-swatch:hover {
  transform: scale(1.15);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.selected-color {
  transform: scale(1.2);
  box-shadow: 0 0 0 3px rgb(var(--v-theme-primary));
}

.section-header {
  text-align: center;
  margin-bottom: 32px;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #718096;
}

.stats-section {
  background: white;
}

.color-section {
  background: #f8f9fa;
}

.trims-section {
  background: #f5f5f5;
}

.comparison-section {
  background: #fafafa;
}

.model-comparison-section {
  background: #f8f9fa;
}

.details-section {
  background: white;
}

.trims-table {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  min-width: 900px;
  display: table;
  width: 100%;
}

/* Override Vuetify table default overflow */
.table-container .v-table {
  overflow: visible !important;
}

.table-container .v-table__wrapper {
  overflow: visible !important;
}

.trim-column-header {
  min-width: 250px;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 960px) {
  .table-container {
    margin: 0 -16px;
    padding: 0 16px;
    width: calc(100% + 32px);
  }
}

.trims-table thead th {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px;
  background: #f8f9fa;
  white-space: nowrap;
}

.trims-table thead th:nth-child(2) {
  /* Price */
  min-width: 100px;
}

.trims-table thead th:nth-child(3) {
  /* Engine */
  min-width: 150px;
}

.trims-table thead th:nth-child(4) {
  /* MPG */
  min-width: 140px;
}

.trims-table thead th:nth-child(5) {
  /* Key Features */
  min-width: 200px;
}

.trims-table thead th:last-child {
  /* Actions */
  min-width: 80px;
}

.trims-table tbody td {
  padding: 12px;
  vertical-align: middle;
}

.trim-image-cell {
  width: 100px;
  height: 60px;
  min-width: 100px;
  max-width: 100px;
  margin-right: 16px;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.trim-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.trim-image-wrapper {
  width: 100px;
  height: 60px;
  min-width: 100px;
  max-width: 100px;
  margin-right: 16px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.trim-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.trim-mobile-details {
  margin-top: 4px;
}

.trim-name {
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.table-cell-ellipsis {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  vertical-align: middle;
}

.selected-badge {
  font-size: 0.7rem;
  font-weight: 500;
  height: 20px;
  padding: 0 6px;
  letter-spacing: -0.01em;
}

.trim-name-cell {
  flex: 1;
  min-width: 0;
}

.trim-name-text {
  font-size: 0.9375rem;
  line-height: 1.3;
  color: inherit;
}

.trim-row {
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 4px solid transparent;
}

.trim-row td:first-child {
  padding-left: 8px;
}

.trim-row:last-child {
  border-bottom: none;
}

.trim-row:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.selected-row {
  background: linear-gradient(
    90deg,
    rgba(25, 118, 210, 0.12) 0%,
    rgba(25, 118, 210, 0.06) 100%
  );
  position: relative;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.selected-chip {
  font-size: 0.7rem;
  font-weight: 500;
  height: 20px;
  padding: 0 6px;
}

.features-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.feature-chip {
  max-width: 150px;
  overflow: hidden;
}

.v-theme--dark {
  .classic-layout {
    background: #121212 !important;
  }

  section {
    background: transparent !important;
  }

  .hero-section {
    background: linear-gradient(135deg, #1a237e 0%, #4a148c 100%) !important;
  }

  .hero-title,
  .hero-price,
  .hero-price-label {
    color: white;
  }

  .section-title {
    color: white;
    background: linear-gradient(135deg, #ffffff, #b0bec5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .stat-label,
  .section-subtitle {
    color: #a0aec0;
  }

  .stat-card {
    background: #2c2c2c !important;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
  }

  .stat-value {
    color: #ffffff !important;
  }

  .stat-icon {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .hero-image {
    background: #2c2c2c !important;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  .hero-section .v-img {
    background: transparent !important;
  }

  .stats-section {
    background: #1e1e1e !important;
  }

  .color-section {
    background: #1e1e1e !important;
  }

  .trims-section {
    background: #121212 !important;
  }

  .trims-table {
    border-color: rgba(255, 255, 255, 0.12);
    background: #1e1e1e;
  }

  .trims-table thead th {
    background: #2d2d2d !important;
    color: white !important;
    background-color: #2d2d2d !important;
  }

  .trims-table thead tr {
    background-color: #2d2d2d !important;
  }

  .trims-table tbody td {
    border-bottom-color: rgba(255, 255, 255, 0.08);
    background-color: #1e1e1e !important;
    color: #e0e0e0 !important;
  }

  .trims-table tbody tr {
    background-color: #1e1e1e !important;
  }

  .trims-table tbody tr:hover {
    background-color: rgba(25, 118, 210, 0.15) !important;
  }

  .trim-image-wrapper {
    background: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.08);
  }

  .trim-image-cell {
    background: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.08);
  }

  .trim-row {
    background: #1e1e1e;
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .trim-row:hover {
    background-color: rgba(25, 118, 210, 0.15);
  }

  .selected-row {
    background: linear-gradient(
      90deg,
      rgba(25, 118, 210, 0.25) 0%,
      rgba(25, 118, 210, 0.15) 100%
    );
    border-left-color: rgb(var(--v-theme-primary));
  }

  .selected-chip,
  .selected-badge {
    background-color: rgb(var(--v-theme-primary)) !important;
    color: white !important;
  }

  .trim-name,
  .trim-name-text {
    color: #e0e0e0;
  }

  .comparison-section {
    background: #1e1e1e !important;
  }

  .model-comparison-section {
    background: #1e1e1e !important;
  }

  .details-section {
    background: #121212 !important;
  }

  .features-chips .v-chip {
    background-color: #2c2c2c !important;
    color: #e0e0e0 !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
  }

  .v-table {
    background-color: #1e1e1e !important;
    color: #e0e0e0 !important;
  }

  .v-table > .v-table__wrapper > table > thead > tr > th {
    background-color: #2c2c2c !important;
    color: #e0e0e0 !important;
  }

  .v-table > .v-table__wrapper > table > tbody > tr > td {
    color: #e0e0e0 !important;
    background-color: #1e1e1e !important;
  }

  .v-table > .v-table__wrapper > table > tbody > tr {
    background-color: #1e1e1e !important;
  }

  .v-table > .v-table__wrapper {
    background-color: #1e1e1e !important;
  }

  .v-list {
    background-color: #1e1e1e !important;
    color: #e0e0e0 !important;
  }

  .v-list-item {
    color: #e0e0e0 !important;
  }

  .v-list-item-title,
  .v-list-item-subtitle {
    color: #e0e0e0 !important;
  }

  .v-list-subheader {
    background-color: #2c2c2c !important;
    color: #ffffff !important;
  }

  .v-btn {
    background-color: transparent !important;
  }

  .v-btn--variant-tonal {
    background-color: rgba(var(--v-theme-primary), 0.12) !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  .v-btn--variant-flat {
    background-color: rgb(var(--v-theme-primary)) !important;
    color: white !important;
  }

  .section-header {
    background-color: transparent !important;
  }

  .section-content {
    background-color: #1e1e1e !important;
  }

  .v-tabs {
    background-color: #1e1e1e !important;
  }

  .v-tab {
    color: #a0aec0 !important;
  }

  .v-tab--selected {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .v-window {
    background-color: transparent !important;
  }

  .v-expansion-panels {
    background-color: transparent !important;
  }

  .v-expansion-panel {
    background-color: #1e1e1e !important;
  }

  .v-expansion-panel-title {
    background-color: #2c2c2c !important;
    color: #e0e0e0 !important;
  }

  .v-expansion-panel-text {
    background-color: #1e1e1e !important;
    color: #e0e0e0 !important;
  }

  .v-chip {
    background-color: #2c2c2c !important;
    color: #e0e0e0 !important;
  }

  .v-chip--variant-tonal {
    background-color: rgba(var(--v-theme-primary), 0.12) !important;
  }

  .v-chip--variant-flat {
    background-color: rgb(var(--v-theme-primary)) !important;
    color: white !important;
  }

  .v-dialog > .v-overlay__content {
    background-color: #1e1e1e !important;
  }

  .v-dialog > .v-overlay__content > .v-card {
    background-color: #1e1e1e !important;
  }

  .v-menu .v-list {
    background-color: #2c2c2c !important;
  }

  .v-tooltip .v-overlay__content {
    background-color: #2c2c2c !important;
    color: #e0e0e0 !important;
  }
}

@media (max-width: 960px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-price {
    font-size: 1.75rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .stat-value {
    font-size: 1.25rem;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 1.5rem;
  }

  .hero-price {
    font-size: 1.5rem;
  }

  .hero-background {
    padding: 40px 0 60px;
  }
}

.trim-row:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

.color-swatch:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
